class AnnotationTypes{
    /**
     * Creates an annotation object for when a student takes the photo of their ID
     * @param {string} pathToFileInS3Bucket - Full path to the photo object in AWS S3
     * @returns {{severity: string, metadata: {path}, title: string}}
     */
    static idPhotoTaken(pathToFileInS3Bucket) {
        return {
            title: 'ID Photo Taken',
            severity: 'green',
            metadata: {
                path: pathToFileInS3Bucket
            }
        }
    }

    /**
     * Creates an annotation object for recording the result of the student's internet speed check
     * @param {boolean} [didPass] - Whether or not the student passed (true === 'passed')
     * @param {number} upload - Integer represent of the upload in Kbps
     * @param {number} download - Integer representation of the download in Kbps
     * @param {number} jitter - Decimal representation of the jitter in ms
     * @param {number} ping - Integer representation of the ping in ms
     * @returns {{severity: (string), metadata: {download, jitter, upload, ping}, title: (string)}}
     */
    static internetSpeedCheck(didPass = true, upload, download, jitter, ping) {
        const annotationTitle = didPass ?
            'Internet Speed Check Passed' :
            'Internet Speed Check Failed';
        const severity = didPass ?
            'green' :
            'yellow';
        return {
            title: annotationTitle,
            severity: severity,
            metadata: {
                upload,
                download,
                jitter,
                ping
            }
        };
    }

    /**
     * Creates an annotation object for when a student is put into "lockdown" (lockdown modality options applied)
     * @param {Object[]} modalityOptionsEnforced - Array of modality options that were applied during lockdown
     * @returns {{severity: string, metadata: {optionsEnforced}, title: string}}
     */
    static lockdown(modalityOptionsEnforced) {
        return {
            title: 'Lockdown',
            severity: 'green',
            metadata: {
                optionsEnforced: modalityOptionsEnforced
            }
        }
    }

    /**
     * Creates an annotation object for when a student takes the photo of their face
     * @param {string} pathToFileInS3Bucket - Full path to the photo object in AWS S3
     * @returns {{severity: string, metadata: {path}, title: string}}
     */
    static facePhotoTaken(pathToFileInS3Bucket) {
        return {
            title: 'Face Photo Taken',
            severity: 'green',
            metadata: {
                path: pathToFileInS3Bucket
            }
        }
    }

    /**
     * Creates an annotation object for when a student completes onboarding
     * @param {string} redirectUrl - Exam URL that the student will be redirected to
     * @returns {{severity: string, metadata: {redirectUrl}, title: string}}
     */
    static onboardingComplete(redirectUrl) {
        return {
            title: 'Onboarding Complete',
            severity: 'green',
            metadata: {
                redirectUrl
            }
        }
    }

    /**
     * Creates an annotation object for when a student advances to a new step in onboarding
     * @param {string} previousStep - Name of the step the student completed
     * @param {string} nextStep - Name of the step the student is moving to
     * @returns {{severity: string, metadata: {previousStep, nextStep}, details: string, title: string}}
     */
    static onboardingStatusChange(previousStep, nextStep) {
        return {
            title: 'Onboarding Status Change',
            severity: 'green',
            metadata: {
                previousStep,
                nextStep
            },
            details: `Student moved from ${previousStep} to ${nextStep} in onboarding.`
        }
    }

    /**
     * Creates an annotation object for when a student accepts both the face and ID photos
     * @param {string} pathToFacePhoto - Path to AWS S3 where the face photo is located
     * @param {string} pathToIdPhoto - Path to AWS S3 where the ID photo is located
     * @returns {{severity: string, metadata: {pathToFacePhoto, pathToIdPhoto}, title: string}}
     */
    static photosAccepted(pathToFacePhoto, pathToIdPhoto) {
        return {
            title: 'Photos Accepted',
            severity: 'green',
            metadata: {
                pathToFacePhoto,
                pathToIdPhoto
            }
        }
    }

    /**
     * Creates an annotation object for when a student begins room scan in onboarding
     * @returns {{severity: string, title: string}}
     */
    static roomScanStart() {
        return {
            title: 'Room Scan Start',
            severity: 'green'
        }
    }

    /**
     * Creates an annotation object for when a student ends room scan in onboarding
     * @returns {{severity: string, title: string}}
     */
    static roomScanStop() {
        return {
            title: 'Room Scan Stop',
            severity: 'green'
        }
    }
}

module.exports = AnnotationTypes;